import { useEffect, useState } from "react";
import Select from "../../../components/select";
import arrow from "../../../assets/svg-icons/blueArrow.svg";
import close from "../../../assets/svg-icons/closeBrand.svg";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { FormatDate, showErrorMessage } from "../../../hooks/constants";
import { useFormik } from "formik";
import * as Yup from "yup";
import Button from "../../../components/button";
import TextArea from "../../../components/textArea";
import Modal from "../../../components/modal";
import { toast } from "react-toastify";
import InlineSpinner from "../../../components/buttonSpinner";
import { useLoadBalancersList, useFloatingIPsList, usePortList } from "../layout/resuableEffect";
import { deleteProjectLoadBalancer, listProjectLoadBalancers, updateFloatingIp } from "../../../hooks/local/userReducer";
import usePartnerNavigation from "../../../hooks/usePartnerNavigation";

const ManageLoadBalancers = () => {

  document.title = "Manage Load Balancers - Cloudvantage";

  const dispatch = useDispatch();
  const { navigate, generatePartnerUrl } = usePartnerNavigation();

  const [searchText, setSearchText] = useState("");
  const projectId = useSelector((state) => state.user.selectedProject).id;

  const [createLoadBalancerModal, setCreateLoadBalancerModal] = useState(false);
  const [manageLoadBalancersModal, setManageLoadBalancersModal] = useState(false);
  const [deleteLoadBalancerModal, setDeleteLoadBalancerModal] = useState(false);
  const [floatingIPModal, setFloatingIPModal] = useState(false);
  const [isDeassociate, setIsDeassociate] = useState(false);
  const [selectedLoadBalancer, setSelectedLoadBalancer] = useState(null);
  const [selectedAssociateLoadBalancer, setSelectedAssociateLoadBalancer] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const loading = useSelector((state) => state.user.loading);

  const loadBalancerList = useLoadBalancersList();
  const floatingIPList = useFloatingIPsList();
  const portList = usePortList(projectId, "");


  // Helper function to get all associated floating IPs
  const getAssociatedFloatingIPs = () => {
    const associatedIPs = new Set();
    floatingIPList.forEach(ip => {
      if (ip.port_id && ip.fixed_ip_address) {
        associatedIPs.add(ip.floating_ip_address);
      }
    });
    return associatedIPs;
  };

  // Helper function to get the VIP port for a load balancer
  const getLoadBalancerPort = (loadBalancer, portList) => {
    const matchingPort = portList.find(port =>
      port.id === loadBalancer.vip_port_id
    );

    return matchingPort
      ? {
          value: matchingPort.id,
          label: `${loadBalancer.name}:${matchingPort.fixed_ips[0].ip_address}`
        }
      : null;
  };

  // Toggle modal for associating floating IP
  const toggleFloatingIPModal = (loadBalancer) => {
    setFloatingIPModal(!floatingIPModal);
    setSelectedAssociateLoadBalancer(loadBalancer);
    setIsDeassociate(false);

    const matchingPort = getLoadBalancerPort(loadBalancer, portList);
    if (matchingPort) {
      manageFloatingIP.setFieldValue('portId', matchingPort.value);
    }

    // Find the floating IP associated with the load balancer's VIP port
    const associatedFloatingIP = floatingIPList.find(ip => ip.port_id === loadBalancer.vip_port_id);
    manageFloatingIP.setFieldValue('ipAddress', associatedFloatingIP ? associatedFloatingIP.id : '');
  };

  // Toggle modal for disassociating floating IP
  const toggleDeassociateFloatingIPModal = (loadBalancer) => {
    setIsDeassociate(!isDeassociate);
    setFloatingIPModal(!floatingIPModal);
    setSelectedAssociateLoadBalancer(loadBalancer);

    const associatedFloatingIP = floatingIPList.find(ip => ip.port_id === loadBalancer.vip_port_id);
    manageFloatingIP.setFieldValue('ipAddress', associatedFloatingIP ? associatedFloatingIP.id : '');
    manageFloatingIP.setFieldValue('portId', loadBalancer.vip_port_id || '');
  };

  // Get port options for the form
  const getPortOptions = (loadBalancer) => {
    if (!loadBalancer || isDeassociate) {
      return [{ label: "Select a port", value: "" }];
    }

    const matchingPort = getLoadBalancerPort(loadBalancer, portList);
    return [
      { label: "Select a port", value: "" },
      ...(matchingPort ? [matchingPort] : portList.filter(port => port.device_id === loadBalancer.id).map(port => ({
        value: port.id,
        label: `${loadBalancer.name}:${port.fixed_ips[0].ip_address}`
      })))
    ];
  };

  // Floating IP options for the select dropdown
  const floatingIPsOptions = Array.isArray(floatingIPList)
    ? [
        { label: "Select a floating IP", value: "" },
        ...floatingIPList.map((ip) => ({
          value: ip.id,
          label: ip.floating_ip_address,
        }))
      ]
    : [{ label: "Select a floating IP", value: "" }];

  const loadBalancerListMap = Array.isArray(loadBalancerList)
    ? loadBalancerList.map((lb) => ({
        name: lb.name || "N/A",
        ipAddress: lb.vip_address || "N/A",
        provider: lb.provider || "N/A",
        operatingStatus: lb.operating_status || "N/A",
        provisioningStatus: lb.provisioning_status || "N/A",
        adminStateUp: lb.admin_state_up ? "Yes" : "No",
        id: lb.id,
        vip_port_id: lb.vip_port_id || "N/A",
        floating_ip: floatingIPList.find(ip => ip.port_id === lb.vip_port_id)?.floating_ip_address || "N/A",
        createdAt: FormatDate(lb.created_at),
      }))
    : [];

  const showManageLoadBalancersModal = (key, data) => {
    setManageLoadBalancersModal(key);
    const loadBalancerDetails = { loadBalancerId: data.id, loadBalancerName: data.name };
    sessionStorage.setItem("selectedLoadBalancer", JSON.stringify(loadBalancerDetails));
  };

  const closeManageLoadBalancersModal = () => {
    setManageLoadBalancersModal(false);
  };

  const handleRedirectToDetails = (index, row) => {
    showManageLoadBalancersModal(index, row);
    navigate("/manageLoadBalancers/loadBalancer-details");
  };

  const toggleCreateLoadBalancerModal = () => {
    navigate("/createLoadBalancer");
  };

  const showDeleteLoadBalancerModal = (key, data) => {
    setDeleteLoadBalancerModal(key);
    setSelectedLoadBalancer(data);
  };

  const closeDeleteLoadBalancerModal = () => {
    setDeleteLoadBalancerModal(false);
  };

  const handleSearchChange = (event) => {
    setSearchText(event.target.value);
    setCurrentPage(1);
  };

  const options = [
    { value: "all-load-balancers", label: "All load balancers" },
    { value: "name", label: "Name" },
    { value: "ipAddress", label: "IP Address" },
    { value: "status", label: "Status" },
  ];

  const [currentPage, setCurrentPage] = useState(1);
  const entriesPerPage = 5;

  const filteredData = loadBalancerListMap?.filter((row) =>
    row?.name?.toLowerCase().includes(searchText.toLowerCase()) ||
    row?.ipAddress?.toLowerCase().includes(searchText.toLowerCase()) ||
    row?.operatingStatus?.toLowerCase().includes(searchText.toLowerCase())
  );

  const indexOfLastEntry = currentPage * entriesPerPage;
  const indexOfFirstEntry = indexOfLastEntry - entriesPerPage;
  const currentEntries = filteredData.slice(indexOfFirstEntry, indexOfLastEntry);
  const totalPages = Math.ceil(filteredData.length / entriesPerPage);

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const createLoadBalancerForm = useFormik({
    initialValues: {
      name: "",
      description: "",
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Name is required"),
    }),
    onSubmit: async (values) => {
      const { name, description } = values;
      const loadBalancerData = {
        projectId,
        name,
        description,
      };
      // Uncomment and implement createLoadBalancer action when available
      // const { payload } = await dispatch(createLoadBalancer(loadBalancerData));
      // if (payload.statuscode === "00") {
      //   await dispatch(listLoadBalancers(projectId));
      //   window.location.reload();
      // }
    },
  });

  const manageFloatingIP = useFormik({
    initialValues: {
      ipAddress: "",
      portId: "",
    },
    validationSchema: Yup.object({
      ipAddress: Yup.string().required("IP address is required"),
      portId: isDeassociate ? Yup.string().optional() : Yup.string().required("Port is required"),
    }),
    onSubmit: async (values) => {
      try {
        const { ipAddress, portId } = values;
        let floatingIpData;
        if (isDeassociate) {
          floatingIpData = {
            projectId,
            floatingIpId: ipAddress,
          };
        } else {
          floatingIpData = {
            projectId,
            floatingIpId: ipAddress,
            port_id: portId,
          };
        }

        const { payload } = await dispatch(updateFloatingIp(floatingIpData));
        if (payload.statuscode === "00") {
          await dispatch(listProjectLoadBalancers(projectId));
          setFloatingIPModal(false);
          toast.success(isDeassociate ? "Floating IP disassociated successfully" : "Floating IP associated successfully");
          window.location.reload();
        } else {
          showErrorMessage(payload);
        }
      } catch (error) {
        showErrorMessage(error);
        console.error(error);
      }
    },
  });

  const handleRowSelection = (id) => {
    setSelectedRows((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedRows([]);
    } else {
      setSelectedRows(currentEntries.map((row) => row.id));
    }
    setSelectAll(!selectAll);
  };

  const handleDeleteSelectedLoadBalancers = async () => {
    if (selectedRows.length > 0) {
      const deleteData = {
        load_balancers: selectedRows,
      };
      const { payload } = await dispatch(deleteProjectLoadBalancer({ deleteData, navigate }));
      if (payload.statuscode === "00") {
        toast.success(payload.message);
      } else {
        showErrorMessage(payload);
      }
    }
  };

  const handleServerAction = async (action, loadBalancerId) => {
    const { payload } = await dispatch(action({ projectId, loadBalancerId }));
    if (payload.statuscode === "00") {
      window.location.reload();
    }
  };

  const deleteLoadBalancer = (loadBalancerId) =>
    handleServerAction(deleteProjectLoadBalancer, loadBalancerId);

  return (
    <div className="min-h-screen px-4 md:px-10 pt-6 pb-10 dark:text-white">
      <p className="crimson-font text-xl font-medium text-primaryBlue">
        Manage your Load Balancers
      </p>

      {loadBalancerListMap?.length > 0 ? (
        <div>
          <div className="w-full flex justify-between items-center gap-4 lg:flex-row flex-col mb-10">
            <div className="w-full grid grid-cols-1 md:grid-cols-12 gap-4 items-end my-10">
              <div className="w-full md:col-span-3">
                <Select label="Filter load balancers" name="load balancers" options={options} />
              </div>
              <div className="md:col-span-4">
                <input
                  type="search"
                  name=""
                  value={searchText}
                  onChange={handleSearchChange}
                  placeholder="Click here for filter or full text search"
                  className="bg-white text-black w-full p-4 text-sm rounded-md border border-[#c4c4c460] active:outline-0 focus:outline-0 placeholder:opacity-70 placeholder:text-xs dark:bg-slate-800 dark:text-white"
                />
              </div>
            </div>

            <div className="w-full md:w-[30%] flex justify-end items-center lg:flex-nowrap flex-wrap gap-4">
              <button
                className="px-4 py-4 text-xs border-[1.5px] border-primaryBlue rounded-md text-primaryBlue w-full md:w-fit text-center"
                onClick={toggleCreateLoadBalancerModal}
              >
                Create Load Balancer
              </button>
            </div>
          </div>

          <div className="overflow-x-auto rounded-md border dark:border-[#c4c4c432]">
            <table className="w-full text-sm text-left overflow-hidden">
              <thead className="bg-white dark:bg-slate-800 px-3 md:px-4 border text-gray-400 dark:border-[#c4c4c432] border-b text-sm font-semibold">
                <tr>
                  <th scope="col" className="px-6 py-4 whitespace-nowrap">
                    <input
                      type="checkbox"
                      checked={selectAll}
                      onChange={handleSelectAll}
                    />
                  </th>
                  <th scope="col" className="px-6 py-4 whitespace-nowrap">
                    Name
                  </th>
                  <th scope="col" className="px-6 py-4 whitespace-nowrap">
                    IP Address
                  </th>
                  <th scope="col" className="px-6 py-4 whitespace-nowrap">
                    Floating IP
                  </th>
                  <th scope="col" className="px-6 py-4 whitespace-nowrap">
                    Operating Status
                  </th>
                  <th scope="col" className="px-6 py-4 whitespace-nowrap">
                    Provisioning Status
                  </th>
                  <th scope="col" className="px-6 py-4 whitespace-nowrap">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-slate-800 px-3 md:px-4 border rounded-b-md dark:border-[#c4c4c432] border-t-2 text-sm">
                {currentEntries.map((row, index) => (
                  <tr
                    key={index}
                    className="dark:hover:bg-[#ffffff05] hover:bg-brandBlue/25 whitespace-nowrap"
                  >
                    <td className="text-start py-4 px-6">
                      <input
                        type="checkbox"
                        checked={selectedRows.includes(row.id)}
                        onChange={() => handleRowSelection(row.id)}
                      />
                    </td>
                    <td
                      className="text-start py-4 px-6 text-primaryBlue hover:underline cursor-pointer"
                      onClick={() => handleRedirectToDetails(index, row)}
                    >
                      {row.name}
                    </td>
                    <td className="text-start py-4 px-6">{row.ipAddress}</td>
                    <td className="text-start py-4 px-6">{row.floating_ip}</td>
                    <td className="text-start py-4 px-6">{row.operatingStatus}</td>
                    <td className="text-start py-4 px-6">{row.provisioningStatus}</td>
                    <td className="text-start py-4 px-6">
                      <button
                        className="text-white p-3 text-xs rounded bg-primaryBlue w-full md:w-fit"
                        onClick={() => showManageLoadBalancersModal(index, row)}
                      >
                        Manage Load Balancer
                      </button>

                      {manageLoadBalancersModal === index && (
                        <div className="w-full fixed h-screen bg-black/70 top-0 left-0 px-4 flex items-end md:items-center justify-center z-50">
                          <div className="bg-white dark:bg-[#141D26] rounded w-full md:w-[30%] dark:text-white max-h-[80%] mb-4 md:mb-0 overflow-auto">
                            <div className="flex justify-between items-center text-sm p-6 border-b dark:border-b-[#c4c4c432]">
                              <div className="flex gap-2 items-center">
                                <span className="dark:text-white">
                                  Manage "{row.name}"
                                </span>
                              </div>
                              <img
                                src={close}
                                onClick={closeManageLoadBalancersModal}
                                className="h-5 cursor-pointer"
                                alt=""
                              />
                            </div>
                            <div className="p-4 grid gap-1">
                              <Link
                                to={generatePartnerUrl("/manageLoadBalancers/loadBalancer-details")}
                                className="flex items-center justify-between hover:rounded border-b dark:border-[#c4c4c424] last:border-none py-4 px-3 hover:bg-primaryBlue/5 transition-all"
                              >
                                <span>View Load Balancer Details</span>
                                <span>
                                  <img src={arrow} alt="" className="" />
                                </span>
                              </Link>

                              <div
                                className="flex items-center justify-between hover:rounded border-b dark:border-[#c4c4c424] last:border-none py-4 px-3 hover:bg-primaryBlue/5 transition-all cursor-pointer"
                                onClick={() => row.floating_ip !== "N/A" ? toggleDeassociateFloatingIPModal(row) : toggleFloatingIPModal(row)}
                              >
                                <span>{row.floating_ip !== "N/A" ? "Disassociate Floating IP" : "Associate Floating IP"}</span>
                                <span>
                                  <img src={arrow} alt="" className="" />
                                </span>
                              </div>

                              <div
                                className="flex justify-self-center text-red-500 rounded-md py-5 px-3 text-center cursor-pointer"
                                onClick={() => {
                                  setDeleteLoadBalancerModal(true);
                                }}
                              >
                                <span className="hover:underline">
                                  Delete Load Balancer
                                </span>
                              </div>
                            </div>
                            <Modal
                              title={"Delete Load Balancer"}
                              isVisible={deleteLoadBalancerModal}
                              onClose={() => setDeleteLoadBalancerModal(false)}
                            >
                              <div className="text-sm text-center pt-6 text-wrap">
                                Are you sure you want to delete{" "}
                                <span className="underline">{row.name}</span> .
                                This action cannot be undone.
                              </div>
                              <div className="grid gap-4 justify-center py-6">
                                <button
                                  className="bg-red-500 text-white text-xs px-6 py-4 rounded-sm w-fit"
                                  onClick={() => deleteLoadBalancer(row.id)}
                                >
                                  Yes, delete {row.name}
                                </button>
                                <button
                                  className="text-[#1DA1F2] text-sm px-6 py-2 hover:underline"
                                  onClick={() => setDeleteLoadBalancerModal(false)}
                                >
                                  Cancel
                                </button>
                              </div>
                            </Modal>
                          </div>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="grid justify-center gap-4 md:flex md:justify-between items-center mt-4">
            <p className="text-gray-400 text-sm">
              Showing{" "}
              <span className="font-medium text-black dark:text-white">
                {indexOfFirstEntry + 1}
              </span>{" "}
              to{" "}
              <span className="font-medium text-black dark:text-white">
                {Math.min(indexOfLastEntry, filteredData?.length)}
              </span>{" "}
              of{" "}
              <span className="font-medium text-black dark:text-white">
                {filteredData?.length}
              </span>{" "}
              results
            </p>
            <div className="flex gap-3">
              <button
                className={`bg-white dark:bg-primaryBlue border dark:border-none rounded-md py-3 px-5 text-sm ${currentPage === 1 ? "opacity-50" : "opacity-100"}`}
                onClick={handlePreviousPage}
                disabled={currentPage === 1}
              >
                Previous
              </button>
              <button
                className={`bg-white dark:bg-primaryBlue border dark:border-none rounded-md py-3 px-5 text-sm ${currentPage === totalPages ? "opacity-50" : "opacity-100"}`}
                onClick={handleNextPage}
                disabled={currentPage === totalPages}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="md:px-52 text-center grid text-gray-500 mt-32">
          <div>It looks like you don't have any Load Balancer yet. Once you create a load balancer in your project, your Load balancers details will appear here.</div>
          <div className="border border-[#c4c4c450] dark:border-[#c4c4c416] rounded-md p-3 flex items-center justify-self-center mt-8 w-fit text-xs cursor-pointer" onClick={toggleCreateLoadBalancerModal}>
            <span>Create Load Balancer</span>
            <span className="ps-2">
              <img src={arrow} alt="" />
            </span>
          </div>
        </div>
      )}

      {/* Create Load Balancer Modal */}
      {createLoadBalancerModal && (
        <div className="fixed top-4 h-screen w-full left-0 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-[#141D26] rounded-md p-4 md:p-10 w-full md:w-1/3 max-h-[80%] overflow-y-scroll">
            <div className="flex items-center justify-between mb-4">
              <p className="text-primaryBlue">Create Load Balancer</p>
              <span onClick={toggleCreateLoadBalancerModal} className="cursor-pointer">
                <img alt="" src={close} className="h-5" />
              </span>
            </div>
            <form className="gap-y-6 grid" onSubmit={createLoadBalancerForm.handleSubmit}>
              <div className="grid gap-2">
                <label className="text-sm font-medium">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={createLoadBalancerForm.values.name}
                  onChange={createLoadBalancerForm.handleChange}
                  onBlur={createLoadBalancerForm.handleBlur}
                  className="bg-white text-black w-full p-4 text-sm rounded-md border border-[#c4c4c460] active:outline-0 focus:outline-0 placeholder:opacity-70 placeholder:text-xs dark:bg-slate-800 dark:text-white"
                />
                {createLoadBalancerForm.errors.name && createLoadBalancerForm.touched.name && (
                  <div className="text-red-500 text-xs">{createLoadBalancerForm.errors.name}</div>
                )}
              </div>
              <TextArea
                label={"Description"}
                type={"text"}
                name={"description"}
                value={createLoadBalancerForm.values.description}
                onBlur={createLoadBalancerForm.handleBlur}
                onChange={createLoadBalancerForm.handleChange}
                onError={
                  createLoadBalancerForm.errors.description &&
                  createLoadBalancerForm.touched.description
                    ? createLoadBalancerForm.errors.description
                    : null
                }
              />
              <div className="flex justify-between items-center gap-2">
                <Button
                  buttonText={"Create Load Balancer"}
                  buttonRole={"submit"}
                />
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Manage Floating IP Associations Modal */}
      {floatingIPModal && (
        <div className="fixed top-0 h-screen w-full left-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-[#141D26] rounded-md p-4 w-full md:w-1/3 max-h-[80%] overflow-y-scroll">
            <div className="flex items-center justify-between mb-4">
              <p className="text-lg font-medium">
                {isDeassociate ? "Disassociate Floating IP" : "Associate Floating IP"}
              </p>
              <span
                onClick={isDeassociate ? toggleDeassociateFloatingIPModal : toggleFloatingIPModal}
                className="cursor-pointer"
              >
                <img alt="" src={close} className="h-5" />
              </span>
            </div>
            <form className="gap-y-6 grid" onSubmit={manageFloatingIP.handleSubmit}>
              <div className="grid gap-2">
                <label className="text-sm font-medium">
                  IP Address <span className="text-red-500">*</span>
                </label>
                <p className="text-sm text-gray-500">
                  {isDeassociate
                    ? "Select the floating IP address to disassociate with the load balancer or a floating IP pool in which to allocate a new floating IP address."
                    : "Select a floating IP address to associate with the load balancer or a floating IP pool in which to allocate a new floating IP address."
                  }
                </p>
                <Select
                  name="ipAddress"
                  options={
                    isDeassociate
                      ? floatingIPsOptions.filter(option =>
                          floatingIPList.some(ip =>
                            ip.floating_ip_address === option.label && ip.port_id && ip.fixed_ip_address
                          )
                        )
                      : floatingIPsOptions.filter(option => !getAssociatedFloatingIPs().has(option.label))
                  }
                  value={manageFloatingIP.values.ipAddress}
                  onBlur={manageFloatingIP.handleBlur}
                  onChange={(e) => manageFloatingIP.setFieldValue("ipAddress", e.target.value)}
                  onError={
                    manageFloatingIP.errors.ipAddress && manageFloatingIP.touched.ipAddress
                      ? manageFloatingIP.errors.ipAddress
                      : null
                  }
                />
              </div>
              {!isDeassociate && (
                <div className="grid gap-2">
                  <label className="text-sm font-medium">
                    VIP Port to be associated <span className="text-red-500">*</span>
                  </label>
                  <Select
                    name="portId"
                    options={getPortOptions(selectedAssociateLoadBalancer)}
                    placeholder="Select a port"
                    value={manageFloatingIP.values.portId}
                    onBlur={manageFloatingIP.handleBlur}
                    onChange={(e) => manageFloatingIP.setFieldValue("portId", e.target.value)}
                    onError={
                      manageFloatingIP.errors.portId && manageFloatingIP.touched.portId
                        ? manageFloatingIP.errors.portId
                        : null
                    }
                  />
                </div>
              )}
              <div className="flex justify-end gap-2">
                <button
                  onClick={isDeassociate ? toggleDeassociateFloatingIPModal : toggleFloatingIPModal}
                  className="px-4 py-4 text-xs border-[1.5px] border-primaryBlue rounded-sm text-primaryBlue cursor-pointer text-center"
                >
                  Cancel
                </button>
                <Button
                  buttonText={isDeassociate ? "Disassociate" : "Associate"}
                  buttonRole={"submit"}
                />
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageLoadBalancers;